import { useState } from 'react';
import {
  Alert,
  ArrowBackIcon,
  AttachMoneyIcon,
  Button,
  Card,
  CardContent,
  CancelIcon,
  CheckCircleIcon,
  Chip,
  Field,
  GroupsIcon,
  HourglassEmptyIcon,
  PersonIcon,
  Skeleton,
  Stack,
  Text,
  Textarea,
} from '@billda/design-system';
import { getErrorMessage } from '@billda/shared-ui';
import { useAllocationDetails } from '../../../hooks/useAllocationDetails';
import { useAdminCancelAllocation } from '../../../hooks/useAdminCancelAllocation';
import { useSitePanelState } from '../../../hooks/useSitePanelState';
import { formatCurrency } from '../../../../../shared/utils/currency';

interface AdminCancelAllocationPanelProps {
  allocationId: string;
  /** Optional success callback - if provided, called instead of using useSitePanelState */
  onSuccess?: () => void;
  /** Optional back callback - shows a back button when provided */
  onBack?: () => void;
}

export function AdminCancelAllocationPanel({
  allocationId,
  onSuccess,
  onBack,
}: AdminCancelAllocationPanelProps) {
  // Only use site panel state if no onSuccess callback provided
  const sitePanelState = useSitePanelState();
  const handleSuccess = onSuccess ?? sitePanelState.closePanel;

  const { data: allocation, error: loadError } =
    useAllocationDetails(allocationId);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { mutate: adminCancel, isPending } = useAdminCancelAllocation();

  const handleConfirm = () => {
    setError(null);
    adminCancel(
      {
        allocationId,
        cancellationNotes: notes || undefined,
      },
      {
        onSuccess: () => {
          handleSuccess();
        },
        onError: err => {
          setError(getErrorMessage(err));
        },
      }
    );
  };

  // Loading state
  if (!allocation && !loadError) {
    return (
      <div data-testid="skeleton-loader" className="p-6">
        <Stack spacing={2}>
          <Skeleton variant="rectangular" height={200} />
          <Skeleton variant="rectangular" height={100} />
        </Stack>
      </div>
    );
  }

  // Error loading allocation
  if (loadError) {
    return (
      <div className="p-6">
        <Alert severity="error">
          {loadError instanceof Error
            ? loadError.message
            : 'Failed to load allocation details'}
        </Alert>
      </div>
    );
  }

  // No data
  if (!allocation) {
    return (
      <div className="p-6">
        <Alert severity="warning">Allocation not found</Alert>
      </div>
    );
  }

  const getStatusConfig = () => {
    switch (allocation.status) {
      case 'InProgress':
        return {
          label: 'In Progress',
          color: 'info' as const,
          icon: <HourglassEmptyIcon className="size-4" aria-hidden="true" />,
        };
      case 'Booked':
        return {
          label: 'Pending',
          color: 'warning' as const,
          icon: <HourglassEmptyIcon className="size-4" aria-hidden="true" />,
        };
      case 'Approved':
        return {
          label: 'Approved',
          color: 'success' as const,
          icon: <CheckCircleIcon className="size-4" aria-hidden="true" />,
        };
      case 'Rejected':
        return {
          label: 'Rejected',
          color: 'error' as const,
          icon: <CancelIcon className="size-4" aria-hidden="true" />,
        };
      default:
        return {
          label: allocation.status,
          color: 'default' as const,
          icon: <HourglassEmptyIcon className="size-4" aria-hidden="true" />,
        };
    }
  };

  const statusConfig = getStatusConfig();
  const isGang = allocation.worker.type === 'Gang';

  return (
    <div className="flex h-full flex-col">
      {/* Scrollable content */}
      <div className="flex-1 overflow-auto p-6">
        <Stack spacing={3}>
          {onBack && (
            <Button
              variant="ghost"
              onClick={onBack}
              size="sm"
              className="self-start"
              data-testid="back-to-allocation-details"
            >
              <ArrowBackIcon className="size-4" aria-hidden="true" />
              Back to Allocation Details
            </Button>
          )}

          {/* Allocation Summary Card */}
          <Card>
            <CardContent className="pt-4">
              <Stack spacing={2}>
                {/* Title and Status */}
                <div className="flex items-start justify-between">
                  <Text variant="h6" as="h2">
                    Allocation Details
                  </Text>
                  <Chip
                    label={statusConfig.label}
                    color={statusConfig.color}
                    icon={statusConfig.icon}
                    size="sm"
                    className="font-semibold"
                  />
                </div>

                {/* Worker Info */}
                <div>
                  <Text
                    variant="caption"
                    color="secondary"
                    className="mb-0.5 block"
                  >
                    Worker
                  </Text>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {isGang ? (
                      <GroupsIcon
                        className="size-4 text-content-secondary"
                        aria-hidden="true"
                      />
                    ) : (
                      <PersonIcon
                        className="size-4 text-content-secondary"
                        aria-hidden="true"
                      />
                    )}
                    <Text variant="body1" className="font-semibold">
                      {allocation.worker.displayName}
                    </Text>
                  </Stack>
                </div>

                {/* Amount */}
                <div className="flex items-center gap-2">
                  <AttachMoneyIcon
                    className="size-4 text-content-secondary"
                    aria-hidden="true"
                  />
                  <div className="flex-1">
                    <Text variant="caption" color="secondary">
                      Amount
                    </Text>
                    <Text variant="h6" className="text-content-accent">
                      {formatCurrency(allocation.amount)}
                    </Text>
                  </div>
                </div>

                {/* Related Item */}
                <div className="border-t border-border-default pt-2">
                  <Text variant="caption" color="secondary">
                    {allocation.relatedItem.itemType === 'LabourItem'
                      ? 'Labour Item'
                      : 'Variation Request'}
                  </Text>
                  <Text variant="body2" className="font-semibold">
                    {allocation.relatedItem.itemName}
                  </Text>
                </div>

                {/* Location */}
                <div>
                  <Text variant="caption" color="secondary">
                    Location
                  </Text>
                  <Text variant="body2">
                    {allocation.plotContext.siteName} -{' '}
                    {allocation.plotContext.plotName}
                  </Text>
                </div>
              </Stack>
            </CardContent>
          </Card>

          {/* Notes TextField */}
          <Field label="Cancellation Notes (optional)">
            <Textarea
              rows={3}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Add any notes about this cancellation..."
              data-testid="cancellation-notes-input"
            />
          </Field>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
        </Stack>
      </div>

      {/* Fixed bottom action area */}
      <div className="border-t border-border-default bg-surface-default p-6">
        {/* Warning Alert */}
        <Alert severity="warning" className="mb-4">
          {allocation.relatedItem.itemType === 'LabourItem' ? (
            <>
              Cancelling this allocation will return{' '}
              <strong>{formatCurrency(allocation.amount)}</strong> to the labour
              item&apos;s available pool. This action cannot be undone.
            </>
          ) : (
            <>
              Cancelling this variation allocation (
              <strong>{formatCurrency(allocation.amount)}</strong>) cannot be
              undone.
            </>
          )}
        </Alert>

        <Button
          variant="danger"
          size="lg"
          fullWidth
          loading={isPending}
          disabled={isPending}
          onClick={handleConfirm}
          data-testid="confirm-cancel-button"
        >
          {isPending ? null : (
            <CancelIcon className="size-4" aria-hidden="true" />
          )}
          {isPending ? 'Cancelling...' : 'Confirm Cancellation'}
        </Button>
      </div>
    </div>
  );
}
